import { useEffect, useMemo, useState } from 'react';
import { listBrands, archiveBrand, deleteBrand } from '../api/brandApi';
import { appConfirm } from '../../../../components/AppConfirmModal';
import { useExportJob } from '../../../../hooks/useExportJob';

const DEFAULT_PAGE_SIZE = 10;

export function useBrandListScreen() {
  const { triggerExport, isExporting } = useExportJob({
    module: 'brand',
    fileFormat: 'XLSX',
  });

  const [companyFilter, setCompanyFilter] = useState('All');
  const [branchFilter, setBranchFilter] = useState('All');
  const [departmentFilter, setDepartmentFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [searchField, setSearchField] = useState('Name');
  const [searchText, setSearchText] = useState('');
  
  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({
    totalCount: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasMore: false,
  });

  const queryParams = useMemo(() => {
    return {
      page,
      page_size: pageSize,
      viewMode: statusFilter.toLowerCase(),
      searchText,
      companyFilter,
      branchFilter,
      departmentFilter,
    };
  }, [page, pageSize, statusFilter, searchText, companyFilter, branchFilter, departmentFilter]);

  const loadBrands = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await listBrands(queryParams);
      const totalCount = response.length;
      const start = (page - 1) * pageSize;
      const paginated = response.slice(start, start + pageSize);
      
      setItems(paginated);
      setMeta({
        totalCount,
        page,
        pageSize,
        hasMore: start + pageSize < totalCount,
      });
    } catch (requestError) {
      setError('Failed to load brands.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadBrands();
  }, [queryParams]);

  const handleArchive = async (id) => {
    try {
      await archiveBrand(id);
      loadBrands();
    } catch (e) {
      setError('Failed to update brand status.');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await appConfirm({
      title: 'Delete Brand',
      message: 'Are you sure you want to permanently delete this brand?',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteBrand(id);
      loadBrands();
    } catch (e) {
      setError('Failed to delete brand.');
    }
  };

  const resetFilters = () => {
    setCompanyFilter('All');
    setBranchFilter('All');
    setDepartmentFilter('All');
    setStatusFilter('Active');
    setSearchField('Name');
    setSearchText('');
    setPage(1);
  };

  const actions = {
    setCompanyFilter,
    setBranchFilter,
    setDepartmentFilter,
    setStatusFilter,
    setSearchField,
    setSearchText,
    setPage,
    resetFilters,
    handleArchive,
    handleDelete,
    refresh: loadBrands,
    triggerExport,
  };

  return {
    state: {
      companyFilter,
      branchFilter,
      departmentFilter,
      statusFilter,
      searchField,
      searchText,
      page,
      pageSize,
      isLoading,
      isExporting,
      error,
      rows: items,
      meta,
    },
    actions,
  };
}

